import React, { createContext, useContext, useState, useEffect } from 'react';

type Theme = 'modern' | 'minimalistic';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  isModern: boolean;
  isMinimalistic: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setTheme] = useState<Theme>(() => {
    const saved = localStorage.getItem('wynder-theme');
    return (saved as Theme) || 'modern';
  });

  useEffect(() => {
    try {
      localStorage.setItem('wynder-theme', theme);
      document.documentElement.setAttribute('data-theme', theme);
    } catch (error) {
      console.error('Failed to save theme to localStorage:', error);
    }
  }, [theme]);

  const toggleTheme = () => {
    const newTheme = theme === 'modern' ? 'minimalistic' : 'modern';
    setTheme(newTheme);
  };

  return (
    <ThemeContext.Provider value={{
      theme,
      toggleTheme,
      isModern: theme === 'modern',
      isMinimalistic: theme === 'minimalistic'
    }}>
      {children}
    </ThemeContext.Provider>
  );
};